// Consent (NGOP-1061) gating and assembly, kept free of React/redux so it can be unit-tested
// without the full component graph (mirrors the backend's SendConsentMailIfAny extraction).

// FrontendSettings values arrive as strings ("true"/"false"), hence the === "true" comparisons.
export type ConsentSettings = Record<string, string | undefined>;

// Each summary exit owns a consent section, configured independently in DS Web Admin: the summary mail
// card is "mail", send-to-shop "retailer", send-to-parent "parent", Send in Shop "salesperson". The
// section selects both the lowercase settings flags and the uppercase translation / consent-id keys.
// Every call site passes it explicitly — there is no default and no fallback between sections, so a card
// cannot render one exit's wording against another exit's flags (NGOP-1510).
export type ConsentSection = "mail" | "retailer" | "parent" | "salesperson";

export interface ConsentOption {
    // Doubles as the gui-translation key and the consent id sent to the backend, e.g. "MAIL_CONSENT_1".
    key: string;
    required: boolean;
}

// The configurable, numbered consents (1..3); the appendix is handled separately (no checkbox).
const CONSENT_NUMBERS = [1, 2, 3];

const isConsentEnabled = (settings: ConsentSettings, section: ConsentSection, n: number) =>
    settings[`${section}_consent_${n}_enabled`] === "true";

// The appendix's translation key, which is also the consent id recorded for it.
export const appendixKey = (section: ConsentSection) => `${section.toUpperCase()}_CONSENT_APPENDIX`;

// Enabled, numbered consents the user can tick, in display order.
export const buildConsentOptions = (settings: ConsentSettings, section: ConsentSection): ConsentOption[] =>
    CONSENT_NUMBERS.filter(n => isConsentEnabled(settings, section, n)).map(n => ({
        key: `${section.toUpperCase()}_CONSENT_${n}`,
        required: settings[`${section}_consent_${n}_required`] === "true",
    }));

export const isAppendixEnabled = (settings: ConsentSettings, section: ConsentSection) =>
    settings[`${section}_consent_appendix_enabled`] === "true";

// Required options the user has not ticked. Empty => send may proceed.
export const unmetRequiredConsents = (options: ConsentOption[], checked: Record<string, boolean>): string[] =>
    options.filter(o => o.required && !checked[o.key]).map(o => o.key);

export interface AppendixPolicy {
    section: ConsentSection;
    // The appendix is informational text with no checkbox. The mail exit records it only when a real
    // consent was ticked, so an appendix-only situation records no approval and sends no consent mail;
    // the retailer and parent exits record it whenever it is configured. Each exit keeps the behaviour
    // it shipped with — changing which consent ids get stored is a GDPR-audit change, not a refactor.
    appendixRequiresTick: boolean;
}

/** One section's checkbox state, plus the required consents the last send attempt found unticked. */
export interface ConsentSelection {
    checked: Record<string, boolean>;
    errors: string[];
}

export const emptyConsentSelection: ConsentSelection = { checked: {}, errors: [] };

// Ticking a box clears its own error at once rather than making the user press Send again. Unticking
// leaves the error list alone: the list records what the last send attempt found, so re-deriving it
// here would flag a box the user has not tried to submit yet.
export const toggleConsentSelection = (
    selection: ConsentSelection,
    key: string,
    isChecked: boolean
): ConsentSelection => ({
    checked: { ...selection.checked, [key]: isChecked },
    errors: isChecked ? selection.errors.filter(k => k !== key) : selection.errors,
});

// The send gate: records which required consents are still unticked and reports whether sending may
// proceed. Kept apart from toggleConsentSelection because this is the only place errors are raised.
export const validateConsentSelection = (
    selection: ConsentSelection,
    options: ConsentOption[]
): { selection: ConsentSelection; canSend: boolean } => {
    const errors = unmetRequiredConsents(options, selection.checked);
    return { selection: { ...selection, errors }, canSend: errors.length === 0 };
};

// Consent ids to record: every ticked option (in display order), plus the appendix per the policy above.
export const collectSelectedConsents = (
    options: ConsentOption[],
    checked: Record<string, boolean>,
    appendixEnabled: boolean,
    { section, appendixRequiresTick }: AppendixPolicy
): string[] => {
    const selected = options.filter(o => checked[o.key]).map(o => o.key);
    if (appendixEnabled && (!appendixRequiresTick || selected.length > 0)) {
        selected.push(appendixKey(section));
    }
    return selected;
};
